/**
 * Quick order: a trade buyer pastes part numbers, one per line, and gets them in the basket.
 *
 * <p>Installers arrive knowing the codes — from a previous order, a design's parts list or the
 * manufacturer's datasheet — so typing them is faster than browsing to them. This file is the
 * pure half: reading what was pasted and deciding whether a product is the one a code names.
 * The block does the lookups.
 *
 * <p>A line is a code and an optional quantity, separated by a comma, semicolon, tab, spaces or
 * an "x": `JAM54D-41-460-LR-TS-AB-MC4, 36`, `REN-420081-B x 8`, `GROWATT-MIN3600-TL-XH`. The same
 * code twice is one line with the quantities added, so pasting two lists together does not
 * produce two basket lines for one product.
 */

export interface QuickOrderLine {
  code: string;
  quantity: number;
}

export interface QuickOrderParse {
  lines: QuickOrderLine[];
  /** Lines that were not blank and could not be read, as typed. */
  invalid: string[];
}

/** More than this in one paste is a spreadsheet, not a quick order. */
export const QUICK_ORDER_MAX_LINES = 50;
export const QUICK_ORDER_MAX_QUANTITY = 9999;

const LINE = /^([A-Za-z0-9][A-Za-z0-9._/+-]*)(?:\s*(?:[,;\t]|\s+[xX×]?|[xX×])\s*(\d+))?\s*[,;]?$/;

export function parseQuickOrder(text: string): QuickOrderParse {
  const byCode = new Map<string, QuickOrderLine>();
  const invalid: string[] = [];

  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const match = LINE.exec(line);
    const quantity = match?.[2] === undefined ? 1 : Number(match[2]);
    if (!match || !Number.isInteger(quantity) || quantity < 1 || quantity > QUICK_ORDER_MAX_QUANTITY) {
      invalid.push(line);
      continue;
    }
    const code = match[1]!;
    const key = normaliseCode(code);
    const existing = byCode.get(key);
    if (existing) existing.quantity = Math.min(existing.quantity + quantity, QUICK_ORDER_MAX_QUANTITY);
    else byCode.set(key, { code, quantity });
  }

  return { lines: [...byCode.values()].slice(0, QUICK_ORDER_MAX_LINES), invalid };
}

/** Codes compare without case or surrounding space: `jam54d…` and `JAM54D…` are one part. */
export function normaliseCode(code: string): string {
  return code.trim().toUpperCase();
}

/**
 * Whether a product is the one `code` names: an exact match on our product code (`sku`) or the
 * manufacturer's part number (`mpn`). Search is fuzzy by design, so its first hit for
 * `REN-420081-B` may well be `REN-420081-S`; only an exact match goes in the basket.
 */
export function matchesCode(product: { sku?: string | null; mpn?: string | null }, code: string): boolean {
  const wanted = normaliseCode(code);
  return [product.sku, product.mpn].some((value) => typeof value === 'string' && normaliseCode(value) === wanted);
}
